import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// MobileStatBar
//
// The desktop status strip lives in PageHeader as a row of chips plus a
// sparkline. At 390pt that row wraps into an unreadable pile, so phones get
// the same three facts as fixed-width tiles instead.
//
// Every tile shows an em dash when its value is null rather than 0. The
// backend deliberately sends `metrics: null` when nothing reported in, and
// collapsing that to zero here would throw away the distinction it went out of
// its way to preserve — "not measuring" versus "measured zero".
// ─────────────────────────────────────────────────────────────────────────────

const DASH = "—";

function Tile({ value, label, tone }) {
  return (
    <Box sx={{
      p: "10px 12px", borderRadius: "12px",
      bgcolor: COLORS.panel, border: `1px solid ${COLORS.border}`,
      minWidth: 0
    }}>
      <Typography sx={{
        color: tone || COLORS.text, fontSize: 18, fontWeight: 600, lineHeight: 1.1,
        fontFamily: COLORS.fontMono, fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
      }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.5, color: COLORS.muted, fontSize: 11, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}

export function MobileStatBar({ metrics, liveCount, destinationCount, uptimeLabel, onAir }) {
  const bitrate = metrics?.bitrate;

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 1
    }}>
      <Tile
        value={bitrate == null ? DASH : (bitrate / 1000).toFixed(2)}
        label="Mbps out"
      />
      <Tile
        value={`${liveCount}/${destinationCount}`}
        label="Sending"
        tone={liveCount > 0 ? COLORS.green : undefined}
      />
      {/* The design's third tile: how long you've been on air. */}
      <Tile
        value={onAir && uptimeLabel ? uptimeLabel.replace(/^0(\d:)/, "$1") : DASH}
        label={onAir ? "On air" : "Off air"}
      />
    </Box>
  );
}
